import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { 
  Search, 
  Filter, 
  X, 
  CalendarIcon,
  Play,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';

const SUBSCRIPTION_STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'QUEUED', label: 'Queued' },
];

const PAGE_SIZE = 20;

const SubscriptionCredits = () => {
  // Data state
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    totalResults: 0,
    totalPages: 0,
    currentPage: 1,
    skip: 0,
    limit: PAGE_SIZE,
    hasMore: false,
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states (pending - not yet applied)
  const [pendingStatusFilter, setPendingStatusFilter] = useState('all');
  const [pendingDateRangeFrom, setPendingDateRangeFrom] = useState(null);
  const [pendingDateRangeTo, setPendingDateRangeTo] = useState(null);
  const [pendingCreditBalanceMax, setPendingCreditBalanceMax] = useState('');
  
  // Applied filter states
  const [appliedStatusFilter, setAppliedStatusFilter] = useState('all');
  const [appliedDateRangeFrom, setAppliedDateRangeFrom] = useState(null);
  const [appliedDateRangeTo, setAppliedDateRangeTo] = useState(null);
  const [appliedCreditBalanceMax, setAppliedCreditBalanceMax] = useState('');
  
  const [showFilters, setShowFilters] = useState(false);

  // Fetch data from API
  const fetchSubscriptionList = useCallback(async (skip = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        skip,
        limit: PAGE_SIZE,
      };
      
      // Add search if present
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      // Add applied filters
      if (appliedStatusFilter && appliedStatusFilter !== 'all') {
        params.subscriptionStatus = appliedStatusFilter;
      }
      
      if (appliedDateRangeFrom) {
        params.renewsOnFrom = appliedDateRangeFrom.toISOString();
      }
      
      if (appliedDateRangeTo) {
        params.renewsOnTo = appliedDateRangeTo.toISOString();
      }
      
      if (appliedCreditBalanceMax) {
        params.creditBalanceLessThan = parseInt(appliedCreditBalanceMax, 10);
      }
      
      const response = await apiService.getSubscriptionList(params);
      
      if (response.success) {
        setShops(response.data.shops || []);
        setPagination(response.data.pagination || {
          totalResults: 0,
          totalPages: 0,
          currentPage: 1,
          skip: 0,
          limit: PAGE_SIZE,
          hasMore: false,
        });
      } else {
        setError(response.message || 'Failed to fetch subscription list');
        setShops([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch subscription list');
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, appliedStatusFilter, appliedDateRangeFrom, appliedDateRangeTo, appliedCreditBalanceMax]);

  // Initial load and when filters change
  useEffect(() => {
    fetchSubscriptionList(0);
  }, [fetchSubscriptionList]);

  // Check if any filters are pending (different from applied)
  const hasPendingChanges = 
    pendingStatusFilter !== appliedStatusFilter ||
    pendingDateRangeFrom !== appliedDateRangeFrom ||
    pendingDateRangeTo !== appliedDateRangeTo ||
    pendingCreditBalanceMax !== appliedCreditBalanceMax;

  // Check if any filters are applied
  const hasAppliedFilters = appliedStatusFilter !== 'all' || appliedDateRangeFrom || appliedDateRangeTo || appliedCreditBalanceMax;

  // Execute filters - apply pending filters
  const executeFilters = () => {
    setAppliedStatusFilter(pendingStatusFilter);
    setAppliedDateRangeFrom(pendingDateRangeFrom);
    setAppliedDateRangeTo(pendingDateRangeTo);
    setAppliedCreditBalanceMax(pendingCreditBalanceMax);
  };

  // Clear all filters
  const clearFilters = () => {
    setPendingStatusFilter('all');
    setPendingDateRangeFrom(null);
    setPendingDateRangeTo(null);
    setPendingCreditBalanceMax('');
    setAppliedStatusFilter('all');
    setAppliedDateRangeFrom(null);
    setAppliedDateRangeTo(null);
    setAppliedCreditBalanceMax('');
  };

  // Handle search on Enter key
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      fetchSubscriptionList(0);
    }
  };

  // Pagination handlers
  const goToNextPage = () => {
    if (pagination.hasMore) {
      const newSkip = pagination.skip + PAGE_SIZE;
      fetchSubscriptionList(newSkip);
    }
  };

  const goToPreviousPage = () => {
    if (pagination.currentPage > 1) {
      const newSkip = Math.max(0, pagination.skip - PAGE_SIZE);
      fetchSubscriptionList(newSkip);
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30';
      case 'QUEUED':
        return 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30';
      case 'CANCELLED':
        return 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/30';
      case 'EXPIRED':
        return 'bg-[#EF4444]/20 text-[#F87171] border-[#EF4444]/30';
      default:
        return 'bg-[#334155] text-[#94A3B8] border-[#475569]';
    }
  };

  const formatCredits = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return '-';
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="subscription-credits-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Subscription & Credits
        </h1>
        <p className="text-[#94A3B8]">
          Manage shop subscriptions and monitor credit balances
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <Input
              placeholder="Search by shop name, contact name, or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="pl-10 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
              data-testid="search-input"
            />
          </div>

          {/* Search Button */}
          <Button
            onClick={() => fetchSubscriptionList(0)}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>

          {/* Filter Toggle Button */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0] ${hasAppliedFilters ? 'border-[#2563EB] text-[#60A5FA]' : ''}`}
            data-testid="filter-toggle"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasAppliedFilters && (
              <Badge className="ml-2 bg-[#2563EB] text-white text-xs px-1.5">
                {[appliedStatusFilter !== 'all', appliedDateRangeFrom || appliedDateRangeTo, appliedCreditBalanceMax].filter(Boolean).length}
              </Badge>
            )}
          </Button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-[#334155]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Subscription Status Filter */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Subscription Status</Label>
                <Select value={pendingStatusFilter} onValueChange={setPendingStatusFilter}>
                  <SelectTrigger className="bg-[#0F172A] border-[#334155] text-[#E2E8F0]" data-testid="status-filter">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#334155]">
                    {SUBSCRIPTION_STATUSES.map(status => (
                      <SelectItem 
                        key={status.value} 
                        value={status.value}
                        className="text-[#E2E8F0] focus:bg-[#334155] focus:text-[#E2E8F0]"
                      >
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range From */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Renews On (From)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-[#0F172A] border-[#334155] text-[#E2E8F0] hover:bg-[#334155]"
                      data-testid="date-from-filter"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#64748B]" />
                      {pendingDateRangeFrom ? format(pendingDateRangeFrom, 'PPP') : <span className="text-[#64748B]">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#1E293B] border-[#334155]" align="start">
                    <Calendar
                      mode="single"
                      selected={pendingDateRangeFrom}
                      onSelect={setPendingDateRangeFrom}
                      initialFocus
                      className="bg-[#1E293B] text-[#E2E8F0]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date Range To */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Renews On (To)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-[#0F172A] border-[#334155] text-[#E2E8F0] hover:bg-[#334155]"
                      data-testid="date-to-filter"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#64748B]" />
                      {pendingDateRangeTo ? format(pendingDateRangeTo, 'PPP') : <span className="text-[#64748B]">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#1E293B] border-[#334155]" align="start">
                    <Calendar
                      mode="single"
                      selected={pendingDateRangeTo}
                      onSelect={setPendingDateRangeTo}
                      initialFocus
                      className="bg-[#1E293B] text-[#E2E8F0]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Credit Balance Max */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Credit Balance Less Than</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={pendingCreditBalanceMax}
                  onChange={(e) => setPendingCreditBalanceMax(e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
                  data-testid="credit-balance-filter"
                />
              </div>
            </div>

            {/* Execute and Clear Buttons */}
            <div className="mt-4 flex justify-end gap-2">
              {(hasAppliedFilters || hasPendingChanges) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155]"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
              <Button
                size="sm"
                onClick={executeFilters}
                disabled={!hasPendingChanges}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="execute-filters"
              >
                <Play className="w-4 h-4 mr-1" />
                Execute
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Results Count and Pagination Info */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[#94A3B8] text-sm">
          {loading ? 'Loading...' : `Showing ${shops.length} of ${pagination.totalResults} shops`}
        </p>
        {pagination.totalPages > 1 && (
          <p className="text-[#94A3B8] text-sm">
            Page {pagination.currentPage} of {pagination.totalPages}
          </p>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-4 mb-6">
          <p className="text-[#F87171]">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchSubscriptionList(0)}
            className="mt-2 border-[#EF4444]/30 text-[#F87171] hover:bg-[#EF4444]/20"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin" />
          <span className="ml-3 text-[#94A3B8]">Loading subscription data...</span>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#334155] hover:bg-transparent">
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Shop Name</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Contact</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Status</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Plan</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Renews On</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap text-right">Sub Credit Bal</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap text-right">Recharge Bal</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap text-right">Total Balance</TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap text-right">
                    <div className="flex flex-col items-end">
                      <span>Purchased</span>
                      <span className="text-[#64748B] text-[10px] font-normal normal-case">(this month)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider whitespace-nowrap text-right">
                    <div className="flex flex-col items-end">
                      <span>Consumed</span>
                      <span className="text-[#64748B] text-[10px] font-normal normal-case">(this month)</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shops.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12 text-[#64748B]">
                      No shops found matching your criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  shops.map((shop) => (
                    <TableRow 
                      key={shop.shopId} 
                      className="border-b border-[#334155]/50 hover:bg-[#334155]/20"
                      data-testid={`shop-row-${shop.shopId}`}
                    >
                      <TableCell className="font-medium text-[#E2E8F0] whitespace-nowrap">
                        {shop.shopName || 'N/A'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>
                          <p className="text-[#E2E8F0] text-sm">{shop.contactPerson || 'N/A'}</p>
                          <p className="text-[#64748B] text-xs">{shop.contactNumber || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {shop.status ? (
                          <Badge variant="outline" className={`${getStatusBadgeStyle(shop.status)} border whitespace-nowrap`}>
                            {shop.status}
                          </Badge>
                        ) : (
                          <span className="text-[#64748B]">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#94A3B8] whitespace-nowrap">
                        {shop.currentPlan?.title || shop.currentPlan?.packName || '-'}
                      </TableCell>
                      <TableCell className="text-[#94A3B8] whitespace-nowrap">
                        {formatDate(shop.renewsOn)}
                      </TableCell>
                      <TableCell className="text-right text-[#60A5FA] font-medium whitespace-nowrap">
                        {formatCredits(shop.subscriptionCreditBalance)}
                      </TableCell>
                      <TableCell className="text-right text-[#4ADE80] font-medium whitespace-nowrap">
                        {formatCredits(shop.rechargeBalance)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-[#E2E8F0] whitespace-nowrap">
                        {formatCredits(shop.totalBalance)}
                      </TableCell>
                      <TableCell className="text-right text-[#A78BFA] font-medium whitespace-nowrap">
                        {formatCredits(shop.purchasedThisMonth)}
                      </TableCell>
                      <TableCell className="text-right text-[#FBBF24] font-medium whitespace-nowrap">
                        {formatCredits(shop.consumedThisMonth)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-[#334155] flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={pagination.currentPage <= 1}
                className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-[#94A3B8] text-sm">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={!pagination.hasMore}
                className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionCredits;
